import * as React from "react";
import { useMemo } from "react";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import { ColumnSort, ColumnFilter, TH } from "security-app/components/common";
import { GLCheckbox } from "common/components";
import { useColumnTogglerContext } from "security-app/components/common/ColumnToggler";
import { usePagination, useSetPagination, useSelectedRows, useSelectedRowsDispatch } from "common/contexts";
import { useGetPriorities } from "security-app/hooks/useAssetsAPI";
import { MachineHeaders, UserHeaders } from "./AssetDetails";
import getVisibleDetailColumnsCount from "./utils/getVisibleDetailColumnsCount";
const ListHeader = ({ assets, showDetailsFor, showSelection = true }) => {
  var _a, _b;
  const { permissions } = useCurrentUser();
  const pagination = usePagination();
  const setPagination = useSetPagination();
  const { columns } = useColumnTogglerContext();
  const { priorities } = useGetPriorities(true);
  const showPriorityColumn = (_b = (_a = columns == null ? void 0 : columns.priority) == null ? void 0 : _a.visible) != null ? _b : true;
  const detailColumnsCount = useMemo(() => getVisibleDetailColumnsCount(columns || {}), [columns]);
  const canManageAssets = useMemo(() => isPermitted(permissions, "asset:edit"), [permissions]);
  const canSelectAssets = showSelection && canManageAssets;
  const selectedAssets = useSelectedRows();
  const selectedAssetsDispatch = useSelectedRowsDispatch();
  const applySort = (field, direction) => {
    setPagination({ ...pagination, orderBy: field, direction });
  };
  const applyFilters = (filterKey, filterValues) => {
    const auxFilters = { ...pagination.filters, [filterKey]: filterValues };
    if (!filterValues || filterValues.length === 0) delete auxFilters[filterKey];
    setPagination({ ...pagination, filters: auxFilters });
  };
  const selectAll = (e) => {
    if (e.target.checked) {
      selectedAssetsDispatch({ type: "add", payload: assets });
    } else {
      selectedAssetsDispatch({ type: "remove", payload: assets });
    }
  };
  const allSelected = useMemo(
    () => assets.length > 0 && selectedAssets.length > 0 && assets.every(
      (asset) => !!selectedAssets.find((sAsset) => asset.id === sAsset.id)
    ),
    [assets, selectedAssets]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("colgroup", null, showSelection && /* @__PURE__ */ React.createElement("col", { style: { width: 32 } }), /* @__PURE__ */ React.createElement(
    "col",
    {
      "data-testid": "assets-list-detail-columns",
      style: { width: "auto" },
      span: detailColumnsCount + 1 + (showPriorityColumn ? 1 : 0)
    }
  ), /* @__PURE__ */ React.createElement("col", { style: { width: 32 } })), /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement("tr", null, showSelection && (canSelectAssets ? /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(GLCheckbox, { checked: allSelected, onChange: selectAll, title: "Select all assets" })) : /* @__PURE__ */ React.createElement(TH, null)), /* @__PURE__ */ React.createElement(TH, null, /* @__PURE__ */ React.createElement(ColumnSort, { field: "name", orderBy: pagination.orderBy, direction: pagination.direction, onSort: applySort }, "Asset Name")), showPriorityColumn && /* @__PURE__ */ React.createElement(TH, { "data-testid": "priority-column-header" }, /* @__PURE__ */ React.createElement(
    ColumnSort,
    {
      field: "priority",
      orderBy: pagination.orderBy,
      direction: pagination.direction,
      onSort: applySort
    },
    /* @__PURE__ */ React.createElement(
      ColumnFilter,
      {
        filterBy: "priority",
        type: "text",
        filterData: priorities.map((priority) => ({
          label: priority.text || priority.priority.toString(),
          value: priority.priority.toString(),
          type: typeof priority.priority
        })),
        appliedFilters: pagination.filters,
        onClose: applyFilters
      },
      "Priority"
    )
  )), /* @__PURE__ */ React.createElement(MachineHeaders, { showDetails: showDetailsFor.machine }), /* @__PURE__ */ React.createElement(UserHeaders, { showDetails: showDetailsFor.user }), /* @__PURE__ */ React.createElement(TH, null))));
};
export default ListHeader;
